/**
 * Formattazioni del pannello Sistema (versione, backup, prove di ripristino).
 *
 * Stanno qui e non dentro le pagine perche' il box in dashboard e la modale
 * dello storico devono scrivere gli stessi valori nello stesso modo: se la
 * dimensione del dump comparisse come "6,15 MB" nel box e "6.452.439 byte"
 * nello storico, il confronto fra i due — che e' il motivo per cui lo storico
 * esiste — andrebbe fatto a mente.
 *
 * Nota sui conteggi: `formatIntero` non mette i due decimali imposti dallo
 * standard di `utils/formatters.ts`. Quella regola vale per importi, quantita'
 * e misure; "201,00 tabelle" o "126,00 file" non sono misure, sono cose
 * contate, e i decimali le renderebbero solo piu' difficili da leggere.
 */

/** Dimensione in byte scritta in MB con due decimali. */
export function formatMB(byte: number | null | undefined): string | null {
  if (byte === null || byte === undefined || !Number.isFinite(byte)) return null;
  return (byte / (1024 * 1024)).toLocaleString('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' MB';
}

/** Conteggio senza decimali, con il separatore delle migliaia italiano. */
export function formatIntero(n: number | null | undefined): string | null {
  if (n === null || n === undefined || !Number.isFinite(n)) return null;
  return n.toLocaleString('it-IT', { maximumFractionDigits: 0 });
}

/**
 * Durata in secondi. Sotto il minuto resta in secondi: e' l'ordine di
 * grandezza attuale di backup (~50 s) e prove di ripristino (~12 s), e
 * "0:12" si legge peggio di "12 s". Sopra il minuto passa a m/s, perche' e'
 * la soglia oltre la quale il numero di secondi da solo non dice piu' niente.
 */
export function formatDurata(sec: number | null | undefined): string | null {
  if (sec === null || sec === undefined || !Number.isFinite(sec)) return null;
  if (sec < 60) return `${Math.round(sec)} s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return s === 0 ? `${m} m` : `${m} m ${s} s`;
}

/**
 * Solo il giorno, letto dalle componenti UTC.
 *
 * Serve per i campi che rappresentano un giorno di calendario e non un istante
 * (la data di una corsa di aggiornamento, salvata a mezzanotte UTC).
 * `toLocaleDateString` userebbe il fuso del browser e in un fuso negativo
 * mostrerebbe il giorno prima.
 */
export function formatGiornoUTC(d: string | null | undefined): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  const gg = String(dt.getUTCDate()).padStart(2, '0');
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  return `${gg}/${mm}/${dt.getUTCFullYear()}`;
}

/**
 * Versione del kernel senza il suffisso della variante.
 *
 * `6.8.0-137-generic` diventa `6.8.0-137`: la variante e' sempre la stessa su
 * questa macchina e nel box Sistema, dove i valori non vanno a capo, quelle
 * otto lettere in piu' spingono la riga fuori dal pannello.
 */
export function formatKernel(k: string | null | undefined): string | null {
  if (!k) return null;
  return k.replace(/-(generic|lowlatency|kvm|aws|gcp|azure)$/, '');
}

/** Data e ora nel formato usato in tutto il pannello. */
export function formatDataOra(d: string | null | undefined): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + dt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Variazione percentuale rispetto al giro precedente.
 *
 * E' il numero che il box in dashboard non puo' dare, perche' guarda un solo
 * record: un dump che perde il 40% da un giro all'altro e' un esito OK con un
 * problema dentro. `null` quando manca un termine di confronto o quando il
 * precedente e' zero (la percentuale non sarebbe definita).
 */
export function variazionePercentuale(
  attuale: number | null | undefined,
  precedente: number | null | undefined,
): number | null {
  if (attuale === null || attuale === undefined || !Number.isFinite(attuale)) return null;
  if (precedente === null || precedente === undefined || !Number.isFinite(precedente)) return null;
  if (precedente === 0) return null;
  return ((attuale - precedente) / precedente) * 100;
}

/** La variazione scritta con il segno: "+1,2%", "−38,4%", "invariato". */
export function formatVariazione(pct: number | null): string | null {
  if (pct === null) return null;
  if (Math.abs(pct) < 0.05) return 'invariato';
  const segno = pct > 0 ? '+' : '−';
  return segno + Math.abs(pct).toLocaleString('it-IT', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }) + '%';
}
